import { FieldPath, FieldValues, useController } from "react-hook-form";
import { Autocomplete, AutocompleteRenderInputParams, FormControl, TextField } from "@mui/material";
import React from "react";
import { RhfInputProps } from "../../../../Forms/RhfInputProps";
import { SelectionListItem } from "../../../../Forms/SelectionListItem";
import { RHFFieldErrorText } from "../../../../Forms/RHFFieldErrorText";
import { textFieldDefaultProps } from "../../../../Forms/TextFieldDefaultProps";
import { isOptionEqualsValue } from "../../../../Utils/strings";
import { MuiSelectChip } from "../../../../Layout/Select/MuiSelectChip";

type Props = {
  disabled?: boolean;
  options: SelectionListItem<string>[];
  label: string;
};

export const RhfAutoComplete = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
>({
  control,
  name,
  disabled = false,
  options,
  label,
}: Props & RhfInputProps<TFieldValues, TName>) => {
  const {
    field,
    fieldState: { error },
    formState: { disabled: isFormDisabled },
  } = useController({
    control,
    name,
  });

  const labels = Object.fromEntries(options.map((o) => [o.Id, o.Label]));

  return (
    <FormControl fullWidth size="small">
      <Autocomplete
        {...field}
        multiple
        noOptionsText="No options available"
        options={options.map((it) => it.Id)}
        getOptionLabel={(optionId) => labels[optionId] ?? "NOT FOUND"}
        isOptionEqualToValue={isOptionEqualsValue}
        onChange={(_, selectedValues) => field.onChange(selectedValues)}
        renderInput={(params: AutocompleteRenderInputParams) => (
          <TextField
            {...params}
            {...textFieldDefaultProps}
            label={label}
            InputProps={{ ...params.InputProps, type: "search" }}
          />
        )}
        renderTags={(values, getTagProps) =>
          values.map((value, index) => (
            <MuiSelectChip
              getTagProps={getTagProps}
              labels={labels}
              index={index}
              valueId={value}
            />
          ))
        }
        size="small"
        filterSelectedOptions
        disableCloseOnSelect
        disabled={isFormDisabled || disabled}
      />
      {!!error && <RHFFieldErrorText fieldError={error} />}
    </FormControl>
  );
};
